import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Crown, Loader2, Sparkles, CheckCircle2, XCircle, Clock } from "lucide-react";

/**
 * AdminElderGrants — bulk-grant Elder Founder status to a list of emails.
 *
 * Paste one email per line (or comma-separated), tap Grant, and the
 * backend loops each row: sends the gold-crown welcome email, mints the
 * next available `elderIndex` (≤ 500), and skips already-Elder users
 * idempotently. Result table shows per-row status so failures don't
 * disappear into the void.
 */
export default function AdminElderGrants() {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elders, setElders] = useState(null);

  const loadElders = async () => {
    try {
      const { data } = await api.get("/admin/elders/list");
      setElders(data);
    } catch { /* not fatal for the tab */ }
  };
  useEffect(() => { loadElders(); }, []);

  const parseEmails = (raw) => {
    return Array.from(
      new Set(
        raw.split(/[\s,;\n]+/)
          .map((s) => s.trim().toLowerCase())
          .filter((s) => s.includes("@") && s.length < 200),
      ),
    );
  };

  const grant = async () => {
    const emails = parseEmails(emailsRaw);
    if (emails.length === 0) return toast.error("Paste at least one email");
    setBusy(true);
    setResults(null);
    try {
      const { data } = await api.post("/admin/elders/grant", { emails });
      setResults(data);
      toast.success(`${data.granted} granted · ${data.skipped} skipped`);
      loadElders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };

  const statusPill = (s) => {
    if (s === "granted") return <span className="inline-flex items-center gap-1 text-primary"><CheckCircle2 className="w-3.5 h-3.5" /> Granted</span>;
    if (s === "already_elder") return <span className="inline-flex items-center gap-1 text-secondary"><Sparkles className="w-3.5 h-3.5" /> Already Elder</span>;
    if (s === "not_found") return <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="w-3.5 h-3.5" /> User not found</span>;
    if (s === "skipped_role") return <span className="inline-flex items-center gap-1 text-foreground/60"><Clock className="w-3.5 h-3.5" /> Not a practitioner</span>;
    if (s === "cap_reached") return <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="w-3.5 h-3.5" /> 500 cap reached</span>;
    return <span className="text-foreground/60">{s}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-elder-grants">
      {/* ── Header stats ────────────────────────────────────────── */}
      <div className="parchment p-5 flex items-center gap-6" data-testid="elder-count-header">
        <div>
          <div className="label-mystic text-secondary">Elder Founders</div>
          <div className="font-serif text-4xl mt-1">
            {elders ? `${elders.count} / ${elders.cap}` : <Loader2 className="w-6 h-6 animate-spin" />}
          </div>
        </div>
        <div className="flex-1 h-2 bg-foreground/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary via-secondary to-primary transition-all duration-500"
            style={{ width: `${elders ? Math.min(100, (elders.count / elders.cap) * 100) : 0}%` }}
          />
        </div>
      </div>

      {/* ── Grant form ──────────────────────────────────────────── */}
      <div className="parchment p-6 space-y-4">
        <div>
          <div className="label-mystic text-secondary">Bulk grant Elder Founder</div>
          <p className="text-foreground/60 text-sm mt-1">
            Paste one email per line (or comma-separated). Practitioners who don&apos;t exist yet
            will be flagged in the results — invite them first, then re-run this. The gold-crown
            welcome email fires automatically for every successful grant.
          </p>
        </div>
        <textarea
          value={emailsRaw}
          onChange={(e) => setEmailsRaw(e.target.value)}
          placeholder={"one@practitioner.com\ntwo@practitioner.com\nthree@practitioner.com"}
          rows={7}
          className="w-full bg-input border border-secondary/25 p-3 rounded-sm font-mono text-sm"
          data-testid="elder-grant-emails"
          spellCheck={false}
        />
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-foreground/55">
            {parseEmails(emailsRaw).length} unique email
            {parseEmails(emailsRaw).length === 1 ? "" : "s"} detected
          </div>
          <button
            onClick={grant}
            disabled={busy || parseEmails(emailsRaw).length === 0}
            className="bg-primary text-primary-foreground disabled:opacity-40 px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs inline-flex items-center gap-2"
            data-testid="elder-grant-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
            {busy ? "Granting…" : "Grant Elder Founder"}
          </button>
        </div>
      </div>

      {/* ── Results table ───────────────────────────────────────── */}
      {results && (
        <div className="parchment p-5" data-testid="elder-grant-results">
          <div className="flex items-baseline justify-between mb-3">
            <div className="label-mystic text-secondary">Results</div>
            <div className="text-xs text-foreground/60">
              {results.granted} granted · {results.skipped} skipped
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-foreground/50">
                <tr>
                  <th className="text-left py-2 px-2">Email</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Elder #</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map((r, i) => (
                  <tr key={i} className="border-t border-foreground/5" data-testid={`elder-grant-row-${i}`}>
                    <td className="py-2 px-2 font-mono text-xs">{r.email}</td>
                    <td className="py-2 px-2">{statusPill(r.status)}</td>
                    <td className="py-2 px-2 text-right font-serif text-secondary">
                      {r.elderIndex ? `#${r.elderIndex}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Existing Elders list ────────────────────────────────── */}
      {elders?.elders && elders.elders.length > 0 && (
        <div className="parchment p-5" data-testid="elder-existing-list">
          <div className="label-mystic text-secondary mb-3">All Elders · sorted by rank</div>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {elders.elders.map((e) => (
              <div key={e.userId} className="flex items-center gap-3 text-sm py-1.5 border-b border-foreground/5 last:border-0">
                <div className="font-serif text-secondary w-14">
                  {e.elderIndex ? `#${String(e.elderIndex).padStart(3, "0")}` : "—"}
                </div>
                <div className="flex-1 truncate">
                  <div>{e.name || "(no name)"}</div>
                  <div className="text-[11px] text-foreground/50 font-mono truncate">{e.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
